import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type MouseEvent,
    type RefObject,
} from 'react';

/** Lerp factor per frame — higher snaps tighter, lower trails further. */
const CURSOR_EASE = 0.2;
/** Matches the bubble's transform/opacity transition (duration-300). */
const BUBBLE_FADE_MS = 300;

interface FollowCursorBubbleOptions {
    /**
     * The bubble only reveals while the pointer is over one of these elements
     * (the trigger text), not anywhere in the section's enlarged hover area.
     */
    triggers: RefObject<HTMLElement | null>[];
    /** Suppress the bubble entirely (e.g. once a section has expanded). */
    enabled?: boolean;
    /**
     * Pin the bubble visible (e.g. a click is in flight). Mouse leave must
     * not hide it, or the loading state would vanish as the pointer moves.
     */
    held?: boolean;
}

/**
 * Drives an Apple-style cursor-following bubble shared by the product's
 * tagged-projects statement and the project's featured-article editorial. A
 * fixed element eases toward the pointer on a rAF loop — writing transforms
 * straight to the DOM, so there is no React re-render per mousemove — and only
 * reveals while the pointer is over the trigger text.
 *
 * Spread `sectionHandlers` on the hover area, attach `bubbleRef` to the fixed
 * bubble's outer node, and gate the inner surface's scale/opacity on
 * `bubbleVisible`. The caller owns the bubble's markup and theme.
 */
export const useFollowCursorBubble = ({
    triggers,
    enabled = true,
    held = false,
}: FollowCursorBubbleOptions) => {
    const [bubbleVisible, setBubbleVisible] = useState(false);
    const bubbleRef = useRef<HTMLDivElement | null>(null);
    const targetRef = useRef({ x: 0, y: 0 });
    const positionRef = useRef({ x: 0, y: 0 });
    const rafRef = useRef<number | null>(null);
    // Defers stopping the easing loop until the fade-out finishes, so the bubble
    // glides to a stop instead of freezing mid-lag at the section edge.
    const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Latest triggers, read by the (stable) handlers without re-creating them.
    const triggersRef = useRef(triggers);
    triggersRef.current = triggers;
    const heldRef = useRef(held);
    heldRef.current = held;

    const clearFadeTimeout = () => {
        if (fadeTimeoutRef.current !== null) {
            clearTimeout(fadeTimeoutRef.current);
            fadeTimeoutRef.current = null;
        }
    };
    const stopBubbleLoop = () => {
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    };

    const stepBubble = useCallback(() => {
        const position = positionRef.current;
        const target = targetRef.current;
        position.x += (target.x - position.x) * CURSOR_EASE;
        position.y += (target.y - position.y) * CURSOR_EASE;
        if (bubbleRef.current) {
            bubbleRef.current.style.transform = `translate3d(${position.x}px, ${position.y}px, 0)`;
        }
        rafRef.current = requestAnimationFrame(stepBubble);
    }, []);

    const onMouseMove = useCallback(
        (event: MouseEvent) => {
            if (!enabled) return;
            // Back in the section before a pending fade finished — keep the loop.
            clearFadeTimeout();
            targetRef.current = { x: event.clientX, y: event.clientY };
            if (rafRef.current === null) {
                // First move after entering: snap, then start easing.
                positionRef.current = { x: event.clientX, y: event.clientY };
                rafRef.current = requestAnimationFrame(stepBubble);
            }
            // Position keeps following across the section, but the bubble only
            // reveals over the trigger text (React bails when unchanged).
            const { clientX: x, clientY: y } = event;
            const overTrigger = triggersRef.current.some((ref) => {
                const r = ref.current?.getBoundingClientRect();
                return (
                    !!r &&
                    x >= r.left &&
                    x <= r.right &&
                    y >= r.top &&
                    y <= r.bottom
                );
            });
            setBubbleVisible(heldRef.current || overTrigger);
        },
        [enabled, stepBubble],
    );

    const onMouseLeave = useCallback(() => {
        // A held bubble (click in flight) stays put — hiding it would take the
        // loading state with it the moment the pointer left the title.
        if (heldRef.current) return;
        // Fade out now, but keep easing toward the last pointer position for the
        // fade's duration so the bubble glides to a stop instead of freezing
        // mid-lag at the section edge; stop the loop once it's invisible.
        setBubbleVisible(false);
        clearFadeTimeout();
        fadeTimeoutRef.current = setTimeout(stopBubbleLoop, BUBBLE_FADE_MS);
    }, []);

    useEffect(() => {
        if (!held) return;
        setBubbleVisible(true);
        clearFadeTimeout();
    }, [held]);

    // Disabling (e.g. the section expanded) hides the bubble and stops the loop.
    useEffect(() => {
        if (enabled) return;
        setBubbleVisible(false);
        stopBubbleLoop();
        clearFadeTimeout();
    }, [enabled]);

    useEffect(
        () => () => {
            stopBubbleLoop();
            clearFadeTimeout();
        },
        [],
    );

    return {
        bubbleRef,
        bubbleVisible,
        sectionHandlers: { onMouseMove, onMouseLeave },
    };
};
